import useAccountInfo from "@/hooks/useAccountInfo";

/**
 * Opt-out, so a missing value counts as on — an account info payload cached
 * from before the setting existed must not hide the feature.
 *
 * `accountInfo` comes from the raw Knex `verify/client` query, which hands back
 * MySQL's `TINYINT(1)` as 0/1 rather than a boolean, so this cannot compare
 * against `false`.
 */
export function resolveCustomerStaffRequestEnabled(
  value: boolean | number | null | undefined,
): boolean {
  if (value == null) return true;

  return Boolean(value);
}

export function useCustomerStaffRequestEnabled(): boolean {
  // Selected rather than pulling the whole store: every calendar block calls
  // this, and an unselected subscription re-renders all of them on any
  // accountInfo change.
  const value = useAccountInfo(
    (state: {
      accountInfo?: { customerStaffRequestEnabled?: boolean | number | null };
    }) => state.accountInfo?.customerStaffRequestEnabled,
  );

  return resolveCustomerStaffRequestEnabled(value);
}

/**
 * A named-staff request only says something when the customer could have named
 * somebody else. On a location with a single employee every booking names them,
 * so the heart marks every appointment and separates none of them (DEV-10414).
 *
 * `null` counts as several, not as one: the count arrives with the calendar
 * payload, and a surface rendering before it must not silently drop a heart
 * that the location has earned.
 *
 * Kept here rather than beside its hook because that hook reads the calendar
 * store, whose import chain reaches the DOM — and the client unit config is
 * deliberately DOM-free.
 */
export function resolveStaffRequestIndicatorVisible({
  isCustomerStaffRequestEnabled,
  locationStaffCount,
}: {
  isCustomerStaffRequestEnabled: boolean;
  locationStaffCount: number | null | undefined;
}): boolean {
  if (!isCustomerStaffRequestEnabled) return false;
  if (locationStaffCount == null) return true;

  return locationStaffCount > 1;
}
